import React, { useEffect, useState, useCallback, useMemo, ForwardedRef, useRef } from "react";
import environment from "../../../../env";
import WidgetCard, { WidgetCardProps } from "../../../components/WidgetCard/WidgetCard";
import { MachineTest } from "../../../../models/machine-test.model";
import moment from "moment";
import { useHistory } from "react-router-dom";
import {
  useTheme,
  ListItem,
  Typography,
  ListItemIcon,
  ListItemText,
  Link,
  List,
  Box,
  ListProps,
  Grid,
  MenuItem,
  Menu,
  Checkbox,
  useMediaQuery,
} from "@material-ui/core";
import { HelpOutline, MoreVert, RemoveCircleOutline, FilterList } from "@material-ui/icons";
import _ from "underscore";
import { enumToLookup } from "../../../../models/lookup.model";

import { MachineTestFrequency } from "../../../../models/machine-test.model";
import Button from "../../../components/Button/Button";
import { EventSearchParams } from "../../../../models/event-search-params.model";
import useMachineTestService from "../../../../hooks/machine-test-service.hook";
import IconButton from "../../../components/IconButton/IconButton";
import { useUserContext } from "../../../../context/user.context";
import ImageWithText from "../../../components/ImageWithText/ImageWithText";
import Atom from "../../../components/Svg/Atom";
import { Permission } from "../../../../models/group.model";
import RetryWidget from "../../../components/ImageCaptions/RetryWidget";
import { CalendarLegendItem } from "../../../components/Calendar/CalendarLegendItem";
import { CalendarLegendSection } from "../../../components/Calendar/CalendarLegendSection";
import { CalendarLegendPopup } from "../../../components/Calendar/CalendarLegendPopup";
import { Components, Virtuoso } from "react-virtuoso";
import { useQuery } from "react-query";
import useDeviceTestService from "../../../../hooks/device-test-service.hook";
import { DeviceTest } from "../../../../models/device-test.model";
import { DeviceTestSearch } from "../../../../models/device-test-search.model";
import useSearchbarService from "../../../../hooks/searchbar-service.hook";

interface ScheduleWidgetOptions {
  criteria: DeviceTestSearch;
}

const defaultScheduleWidgetOptions: ScheduleWidgetOptions = {
  criteria: {
    itemsPerPage: 1000
  },
};

const machineTestFrequencies = enumToLookup(MachineTestFrequency);

// One cell of a two-column list row (tablet - see listColumns).
const listCellStyle: React.CSSProperties = { flex: "0 0 50%", width: "50%", minWidth: 0 };

export interface ScheduleWidgetProps extends WidgetCardProps {
  onRemoveWidget?: () => void;
}

function ScheduleWidget(props: ScheduleWidgetProps) {
  const { getDeviceTests, hasTestPermission } = useDeviceTestService();
  const { org, facility, machine, hasPermission } = useUserContext();
  const [retry, setRetry] = useState(0);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [statusFilter, setStatusFilter] = useState({ due: true, overdue: true });
  const [scheduleWidgetOptions] = useState<ScheduleWidgetOptions>(defaultScheduleWidgetOptions);
  const { searchbarFilter } = useSearchbarService();
  const history = useHistory();
  const theme = useTheme();

  // Mirrors Button's own "containedPrimary" gradient shape (Button.tsx), sourced from the
  // theme's `info` palette color instead of a hardcoded hex, for the overdue date icon (Story #9501).
  const overdueDateIconStyle = useMemo(
    () => ({ background: `linear-gradient(45deg, ${theme.overdueAccent.main} 30%, ${theme.overdueAccent.light} 90%)` }),
    [theme]
  );

  // Legend popup for the date icon colors (Story #9501, AC1/AC2), mirroring the Calendar
  // widget's own Legend button/popup: orange is the default "Due" state, light blue is
  // "Overdue" once >=1 test in the frequency group is past due.
  const legendContent = useMemo(
    () => (
      <CalendarLegendSection title="ZapThruIT Legend">
        <CalendarLegendItem label="Due" color={theme.palette.primary.main} shape="circle" />
        <CalendarLegendItem label="Overdue (Incomplete)" color={theme.overdueAccent.main} shape="circle" />
      </CalendarLegendSection>
    ),
    [theme]
  );

  const options = scheduleWidgetOptions || defaultScheduleWidgetOptions;

  const {
    isLoading: testsLoading,
    isError: testsError,
    data: tests,
  } = useQuery(["home/widget/schedule/tests", searchbarFilter, org, retry], {
    queryFn: async () => {
      if (!options.criteria || !searchbarFilter || !org) {
        return Promise.resolve([]);
      }

      options.criteria.facilityId = searchbarFilter.facilityId;
      options.criteria.machineId = searchbarFilter.machineId;
      options.criteria.page = 1;
      options.criteria.itemsPerPage = 1000;
      const orgId = org ? org.id : searchbarFilter.orgId!;

      const result = await getDeviceTests(orgId, options.criteria);
      return result.items.filter(test => hasTestPermission(test.facilityId, test.machineTypeId, test.frequency));
    },
    refetchOnMount: 'always',
  });

  // Memoize function result by side affect of value
  const testsByMachineFrequency = useMemo(() => {
    if (!tests?.length) {
      return [];
    }

    // Group by machine + frequency (the Test Frequency Group) so that overdue tests for the
    // same machine+frequency share one row regardless of exactly how overdue each one is -
    // but keep each test's own due date around (`dated` below) so a row's displayed count and
    // "run tests" action only ever cover the tests that actually belong to that row's date,
    // instead of every test the machine+frequency has ever had.
    const groupsByKey = new Map<
      string,
      {
        key: string;
        frequency?: string;
        machineName: string;
        facilityName: string;
        dated: { test: DeviceTest; dueAt: moment.Moment }[];
      }
    >();

    // `getDeviceTests` (v2/orgs/:orgId/machine-tests) never populates `DeviceTest.overdue` -
    // that flag only exists on the /qa endpoint's response. dueAt here is computed server-side
    // as (last completed run || startDate) + frequency, so a past dueAt already means "still
    // outstanding" - a completed test's dueAt would have advanced to its next cycle. Comparing
    // dueAt against today is therefore both necessary (the flag isn't there) and sufficient
    // (it inherently captures "overdue and uncompleted").
    const today = moment.utc().startOf("date");

    tests.forEach((i) => {
      const key = `${i.frequency}_${i.machineId}`;
      const dueAt = moment.utc(i.dueAt);

      let group = groupsByKey.get(key);

      if (!group) {
        group = {
          key,
          frequency: machineTestFrequencies.find((freq) => freq.id === i.frequency)?.name,
          machineName: i.machineName,
          facilityName: i.facilityName,
          dated: [],
        };
        groupsByKey.set(key, group);
      }

      // A null/missing dueAt produces an invalid moment - it has no due date to group, sort,
      // or display by, so leave it out entirely rather than let it poison moment.min() below
      // (an invalid moment there wins permanently over valid ones).
      if (!dueAt.isValid()) {
        return;
      }

      group.dated.push({ test: i, dueAt });
    });

    // Groups where every test had a null/invalid dueAt have nothing to sort or display by -
    // skip them rather than let moment.min([]) default to "now" and mislabel them as due today.
    const t = Array.from(groupsByKey.values())
      .filter((group) => group.dated.length > 0)
      .map((group) => {
        const overdueEntries = group.dated.filter((d) => d.dueAt.isBefore(today));
        const overdue = overdueEntries.length > 0;
        // Overdue rows combine every overdue test regardless of exactly how overdue it is;
        // non-overdue rows show (and only include) the tests sharing the single earliest
        // upcoming due date, so two different future due dates don't collapse into one row.
        const dueDate = moment.min((overdue ? overdueEntries : group.dated).map((d) => d.dueAt));
        const relevantEntries = overdue ? overdueEntries : group.dated.filter((d) => d.dueAt.isSame(dueDate, "date"));

        return {
          key: group.key,
          frequency: group.frequency,
          machineName: group.machineName,
          facilityName: group.facilityName,
          overdue,
          dueDate: dueDate.format("YYYY-MM-DD"),
          dueMonth: dueDate.format("MMM").toUpperCase(),
          dueDay: dueDate.format("D"),
          tests: relevantEntries.map((d) => d.test),
        };
      });

    return t.sort((a, b) => moment(a.dueDate).diff(moment(b.dueDate), "days"));
  }, [tests]);

  // Frontend-only filter (Story: ZapThruIT filter) - the schedule rows are already fetched and
  // grouped client-side above, so filtering by Due/Overdue status is applied here rather than
  // as a server query criteria.
  const filteredTestsByMachineFrequency = useMemo(
    () => testsByMachineFrequency.filter((item) => (item.overdue ? statusFilter.overdue : statusFilter.due)),
    [testsByMachineFrequency, statusFilter]
  );

  // Counts are based on the full (unfiltered) row set so a toggled-off checkbox doesn't hide its
  // own count.
  const statusCounts = useMemo(() => {
    const overdue = testsByMachineFrequency.filter((item) => item.overdue).length;
    return { due: testsByMachineFrequency.length - overdue, overdue };
  }, [testsByMachineFrequency]);

  const handleToggleStatusFilter = useCallback(
    (key: "due" | "overdue") => () => {
      setStatusFilter((prev) => ({ ...prev, [key]: !prev[key] }));
    },
    [setStatusFilter]
  );

  const handleRunTests = useCallback(
    (tests: DeviceTest[]) => () => {
      history.push(`/tests/runs/${tests[0].machineId}`, {
        previousLocation: { pathname: `/` },
        tests: tests,
      });
    },
    [history]
  );

  const handleRetry = useCallback(() => {
    setRetry((prev) => prev + 1);
  }, []);

  const handleOpenMenu = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setMenuAnchorEl(event.currentTarget);
    },
    [setMenuAnchorEl]
  );

  const handleCloseMenu = useCallback(() => {
    setMenuAnchorEl(null);
  }, [setMenuAnchorEl]);

  const renderListItem = useCallback(
    (index: number) => {
      const item = filteredTestsByMachineFrequency[index];
      const getSecondaryText = (item: any) => {
        const text = `${item.tests.length} ${item.frequency} test${item.tests.length > 1 ? "s" : ""}`;

        if (searchbarFilter.facilityId) {
          return text;
        }

        return `${item.facilityName}\n${text}`;
      };

      return (
        <>
          <ListItemIcon>
            <Button color="primary" style={item.overdue ? overdueDateIconStyle : undefined}>
              <div>
                <div className="month">
                  <Typography variant="caption">{item.dueMonth}</Typography>
                </div>
                <div className="day">{item.dueDay}</div>
              </div>
            </Button>
          </ListItemIcon>
          <ListItemText
            primaryTypographyProps={{ style: { overflow: "hidden" } }}
            primary={item.machineName}
            // Use whiteSpace: "pre-line" so all white space is removed except new lines.
            // Some machine and/or facility names have leading white space, and "pre-line"
            // prevents that leading white space from rendering.
            secondaryTypographyProps={{ variant: "caption", style: { whiteSpace: "pre-line" } }}
            secondary={getSecondaryText(item)}
          />
        </>
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filteredTestsByMachineFrequency]
  );

  // On tablet the widget sits full-width below the Calendar (see Home.tsx scheduleColumn), which
  // leaves room for two items per row. Desktop (narrow column) and phone keep one column.
  const listColumns = useMediaQuery(theme.breakpoints.only("sm")) ? 2 : 1;

  // Virtuoso renders rows: one item per row, or up to `listColumns` items side by side.
  const renderListRow = useCallback(
    (rowIndex: number) => {
      if (listColumns === 1) {
        return renderListItem(rowIndex);
      }

      const start = rowIndex * listColumns;
      return filteredTestsByMachineFrequency.slice(start, start + listColumns).map((item, offset) => (
        <ListItem key={item.key} component="div" dense button style={listCellStyle} onClick={handleRunTests(item.tests)}>
          {renderListItem(start + offset)}
        </ListItem>
      ));
    },
    [listColumns, renderListItem, filteredTestsByMachineFrequency, handleRunTests]
  );

  const listComponents: Components = useMemo(() => {
    return {
      List: React.forwardRef(({ style, children }: ListProps, listRef: ForwardedRef<HTMLDivElement>) => {
        return (
          <List disablePadding style={{ ...style }} component="div" ref={listRef}>
            {children}
          </List>
        );
      }),

      Item: ({ children, ...props }) => {
        if (listColumns > 1) {
          // Each cell is its own clickable ListItem (see renderListRow).
          return (
            <div {...props} style={{ display: "flex" }}>
              {children}
            </div>
          );
        }

        const index = props["data-item-index"];
        const item = filteredTestsByMachineFrequency[index];

        return (
          <ListItem component="div" {...props} dense button onClick={handleRunTests(item.tests)}>
            {children}
          </ListItem>
        );
      },
    };
  }, [listColumns, handleRunTests, filteredTestsByMachineFrequency]);

  const ScheduleItemList = useMemo(
    () =>
      testsError ? (
        <RetryWidget onRetry={handleRetry} />
      ) : !tests?.length ? (
        <ImageWithText
          image={<Atom size="80px" />}
          title="All caught up!"
          subheader="Tests grouped by due date will show here, so you can quickly run the tests that are due."
        />
      ) : !filteredTestsByMachineFrequency.length ? (
        <ImageWithText
          image={<Atom size="80px" />}
          title="No matching tests"
          subheader="No tests match the selected filter. Try adjusting the filter above."
        />
      ) : (
        <Box flex={1} minHeight={0} ml={-2} mr={-2}>
          <Virtuoso
            components={listComponents}
            totalCount={Math.ceil(filteredTestsByMachineFrequency.length / listColumns)}
            itemContent={renderListRow}
          />
        </Box>
      ),
    [testsError, handleRetry, listComponents, renderListRow, listColumns, tests?.length, filteredTestsByMachineFrequency.length]
  );

  const { onRemoveWidget, ...rest } = props;

  const defaultWidgetCardProps = {
    style: { height: "100%", display: "flex", flexDirection: "column" as const },
    ...rest,
  };

  const widgetMenuButtonRef = useRef(null);
  const filterMenuButtonRef = useRef(null);

  return (
    <WidgetCard
      id="schedule-widget"
      header="ZapThruIT"
      loading={testsLoading}
      {...defaultWidgetCardProps}
      actions={
        <Grid container>
          <Grid item>
            <Box paddingRight={1}>
              <CalendarLegendPopup trigger={<Button variant="outlined">Legend</Button>}>{legendContent}</CalendarLegendPopup>
            </Box>
          </Grid>
          <Grid item ref={filterMenuButtonRef}>
            <IconButton id="schedule-widget-filter-button" aria-label="filter schedule" aria-haspopup="true" onClick={handleOpenMenu}>
              <FilterList />
            </IconButton>
            <Menu
              id="schedule-filter-menu"
              keepMounted
              anchorEl={filterMenuButtonRef.current}
              open={Boolean(menuAnchorEl?.id === "schedule-widget-filter-button")}
              onClose={handleCloseMenu}
              disableScrollLock={true}
              container={filterMenuButtonRef.current}
              getContentAnchorEl={null}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
            >
              <MenuItem button onClick={handleToggleStatusFilter("due")}>
                <ListItemIcon style={{ marginLeft: "-8px", marginRight: "8px" }}>
                  <Checkbox checked={statusFilter.due} />
                </ListItemIcon>
                <ListItemText primary={`Due (${statusCounts.due})`} />
              </MenuItem>

              <MenuItem button onClick={handleToggleStatusFilter("overdue")}>
                <ListItemIcon style={{ marginLeft: "-8px", marginRight: "8px" }}>
                  <Checkbox checked={statusFilter.overdue} />
                </ListItemIcon>
                <ListItemText primary={`Overdue (${statusCounts.overdue})`} />
              </MenuItem>
            </Menu>
          </Grid>

          <Grid item>
            <Link
              component={IconButton}
              color="textSecondary"
              target="_blank"
              rel="noopener noreferrer"
              href={`${environment.userManualBaseUrl}/ZapThruIT.pdf`}
              aria-label="ZapThruIT help"
            >
              <HelpOutline />
            </Link>
          </Grid>

          {onRemoveWidget != null && (
            <Grid item ref={widgetMenuButtonRef}>
              <IconButton id="schedule-widget-menu-button" aria-label="schedule widget menu" aria-haspopup="true" onClick={handleOpenMenu}>
                <MoreVert />
              </IconButton>
              <Menu
                id="schedule-menu"
                keepMounted
                anchorEl={widgetMenuButtonRef.current}
                open={Boolean(menuAnchorEl?.id === "schedule-widget-menu-button")}
                onClose={handleCloseMenu}
                disableScrollLock={true}
                container={widgetMenuButtonRef.current}
                getContentAnchorEl={null}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
              >
                <MenuItem button onClick={onRemoveWidget}>
                  <ListItemIcon style={{ marginLeft: "-8px", marginRight: "8px" }}>
                    <RemoveCircleOutline />
                  </ListItemIcon>
                  <ListItemText primary="Remove widget" />
                </MenuItem>
              </Menu>
            </Grid>
          )}
        </Grid>
      }
    >
      {ScheduleItemList}
    </WidgetCard>
  );
}

export default React.memo(ScheduleWidget);
